import React, { useState, useEffect, useRef } from 'react';
import {
  Input,
  InputDiv,
  Label,
  SearchOptions,
  Option
} from '../../../../../components/styles/select-style';

interface OptionType {
  id: number | string;
  name: string;
  description?: string;
  productCode?: string;
}

interface Props {
  label: string;
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  options: OptionType[];
  disabled?: boolean;
}

const AutocompleteSelect: React.FC<Props> = ({
  label,
  value,
  onChange,
  options,
  disabled
}) => {
  const [search, setSearch] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);

  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    const selected = options.find(opt => String(opt.id) === String(value));
    setSearch(selected?.name || '');
    setIsTyping(false);
    setShowOptions(false);
  }, [value, options]);

  const filteredOptions = isTyping
    ? options.filter(opt =>
        opt.name.toLowerCase().includes(search.toLowerCase()) ||
        (opt.productCode && String(opt.productCode).toLowerCase().includes(search.toLowerCase()))
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (opt: OptionType) => {
    setSearch(opt.name);
    setShowOptions(false);
    setIsTyping(false);
    onChange(opt.id);
  };

  return (
    <InputDiv>
      <Label>{label}</Label>

      <div style={{ position: 'relative', width: '100%' }}>
        <Input
          ref={inputRef}
          type="text"
          value={search}
          disabled={disabled}
          placeholder={disabled ? 'Selecione antes...' : 'Todas'}
          onFocus={() => {
            if (disabled) return;
            setShowOptions(true);
            setIsTyping(false);
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsTyping(true);
            setShowOptions(true);
            setSelectedIndex(0);
          }}
          onBlur={() => {
            setTimeout(() => {
              setShowOptions(false);
              if (search === '') {
                onChange(null);
              } else {
                const selected = options.find(opt => String(opt.id) === String(valueRef.current));
                setSearch(selected?.name || '');
              }
            }, 200);
          }}
          style={{ textAlign: 'left', paddingRight: value ? '30px' : '10px' }}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSearch('');
              onChange(null);
            }}
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>

      {showOptions && !disabled && filteredOptions.length > 0 && (
        <SearchOptions ref={optionsRef}>
          {filteredOptions.map((opt, index) => (
            <Option
              key={opt.id}
              isSelected={index === selectedIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={() => handleSelect(opt)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '8px 12px', gap: '4px' }}
            >
              <div style={{ fontWeight: 500 }}>
                {opt.name}
                {opt.productCode ? ` - Cód. ${opt.productCode}` : ''}
              </div>
              {opt.description && (
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  marginTop: '2px'
                }}>
                  Descrição: {opt.description}
                </div>
              )}
            </Option>
          ))}
        </SearchOptions>
      )}
    </InputDiv>
  );
};

export default AutocompleteSelect;